import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import type { TaskRow, MilestoneRow } from "@shared/types";
import { parseDateLocal } from "@/lib/format";
import { ymd } from "@/lib/calendar";
import { typeMeta } from "@/lib/workItemType";
import { taskProgress } from "@/lib/criticalPath";

type Users = ReadonlyMap<string, { name: string; image: string | null }>;
type Edge = { blockerId: string; blockedId: string };
type Zoom = "week" | "month" | "quarter";
type GroupBy = "none" | "assignee" | "milestone" | "epic";

const PX_PER_DAY: Record<Zoom, number> = { week: 26, month: 9, quarter: 3.4 };
const ROW_H = 34;
const LABEL_W = 220;
const HEADER_H = 30;
const DAY_MS = 86_400_000;

function dayNum(d: Date): number {
  return Math.floor(
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / DAY_MS,
  );
}
function addDaysStr(dateStr: string, n: number): string {
  const d = parseDateLocal(dateStr)!;
  d.setDate(d.getDate() + n);
  return ymd(d);
}

type Row =
  | { kind: "group"; key: string; label: string }
  | { kind: "task"; key: string; task: TaskRow; y: number };

export function TaskTimeline({
  projectId,
  tasks,
  users,
  milestones,
  epicNames,
  edges,
  criticalIds,
  showProgress,
}: {
  projectId: string;
  tasks: TaskRow[];
  users: Users;
  milestones: MilestoneRow[];
  epicNames?: ReadonlyMap<string, string>;
  edges: Edge[];
  /** Gantt mode: task ids on the critical path (highlighted). */
  criticalIds?: ReadonlySet<string>;
  /** Gantt mode: render a percent-complete fill on each bar. */
  showProgress?: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [zoom, setZoom] = useState<Zoom>("month");
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  // Live drag state: which task, which edge, and the day-delta preview.
  const [drag, setDrag] = useState<{
    id: string;
    mode: "move" | "l" | "r";
    startX: number;
    days: number;
  } | null>(null);

  const pxPerDay = PX_PER_DAY[zoom];

  const patch = useMutation({
    mutationFn: (v: { taskId: string; startDate?: string; dueDate?: string }) =>
      api.patchTask(v.taskId, { startDate: v.startDate, dueDate: v.dueDate }),
    onSuccess: () => invalidateForTask(qc, { projectId }),
  });

  // Date range across all dated tasks (+ milestones), padded a few days.
  const { rangeStart, totalDays } = useMemo(() => {
    const nums: number[] = [];
    for (const t of tasks) {
      if (t.startDate) nums.push(dayNum(parseDateLocal(t.startDate)!));
      if (t.dueDate) nums.push(dayNum(parseDateLocal(t.dueDate)!));
    }
    for (const m of milestones)
      if (m.dueDate) nums.push(dayNum(parseDateLocal(m.dueDate)!));
    const today = dayNum(new Date());
    nums.push(today - 7, today + 21);
    const min = Math.min(...nums) - 3;
    const max = Math.max(...nums) + 3;
    return { rangeStart: min, totalDays: max - min + 1 };
  }, [tasks, milestones]);

  const xForDay = (day: number) => (day - rangeStart) * pxPerDay;
  const xForDate = (dateStr: string) =>
    xForDay(dayNum(parseDateLocal(dateStr)!));

  // Build the ordered row list (group headers + tasks), assigning each task a
  // y so dependency connectors can find both endpoints.
  const { rows, taskRowY } = useMemo(() => {
    const out: Row[] = [];
    const yById = new Map<string, number>();
    let i = 0;
    const pushTask = (t: TaskRow) => {
      const y = i * ROW_H;
      out.push({ kind: "task", key: t.id, task: t, y });
      yById.set(t.id, y);
      i++;
    };
    if (groupBy === "none") {
      tasks.forEach(pushTask);
    } else {
      const groups = new Map<string, { label: string; items: TaskRow[] }>();
      for (const t of tasks) {
        let key: string;
        let label: string;
        if (groupBy === "assignee") {
          const id = t.assigneeIds?.[0] ?? t.assigneeId ?? "";
          key = id || "__none__";
          label = id ? (users.get(id)?.name ?? id) : "Unassigned";
        } else if (groupBy === "epic") {
          key = t.epicId ?? "__none__";
          label = t.epicId ? (epicNames?.get(t.epicId) ?? "Epic") : "No epic";
        } else {
          key = t.milestoneId ?? "__none__";
          label =
            milestones.find((m) => m.id === t.milestoneId)?.name ??
            "No milestone";
        }
        const g = groups.get(key) ?? { label, items: [] };
        g.items.push(t);
        groups.set(key, g);
      }
      for (const [key, g] of groups) {
        out.push({ kind: "group", key: `g-${key}`, label: g.label });
        i++;
        g.items.forEach(pushTask);
      }
    }
    return { rows: out, taskRowY: yById };
  }, [tasks, groupBy, users, milestones]);

  // Window-level pointer tracking while dragging a bar/handle.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const days = Math.round((e.clientX - drag.startX) / pxPerDay);
      setDrag((d) => (d && d.days !== days ? { ...d, days } : d));
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && d.days !== 0) commitDrag(d);
        return null;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag?.id, drag?.mode, pxPerDay]);

  function commitDrag(d: NonNullable<typeof drag>) {
    const t = tasks.find((x) => x.id === d.id);
    if (!t || !t.startDate || !t.dueDate) return;
    if (d.mode === "move") {
      patch.mutate({
        taskId: d.id,
        startDate: addDaysStr(t.startDate, d.days),
        dueDate: addDaysStr(t.dueDate, d.days),
      });
    } else if (d.mode === "l") {
      const ns = addDaysStr(t.startDate, d.days);
      if (ns <= t.dueDate) patch.mutate({ taskId: d.id, startDate: ns });
    } else if (d.mode === "r") {
      const nd = addDaysStr(t.dueDate, d.days);
      if (nd >= t.startDate) patch.mutate({ taskId: d.id, dueDate: nd });
    }
  }

  const width = totalDays * pxPerDay;
  const bodyH = rows.length * ROW_H;
  const todayX = xForDay(dayNum(new Date()));

  // Month label ticks across the header.
  const monthTicks = useMemo(() => {
    const ticks: { x: number; label: string }[] = [];
    const start = new Date((rangeStart + 0.5) * DAY_MS);
    const d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (dayNum(d) <= rangeStart + totalDays) {
      ticks.push({
        x: xForDay(dayNum(d)),
        label: d.toLocaleDateString(undefined, {
          month: "short",
          year: "2-digit",
        }),
      });
      d.setMonth(d.getMonth() + 1);
    }
    return ticks;
  }, [rangeStart, totalDays, pxPerDay]);

  function barGeom(t: TaskRow) {
    if (!t.startDate || !t.dueDate) return null;
    const left = xForDate(t.startDate);
    const right = xForDate(t.dueDate) + pxPerDay; // due day inclusive
    return { left, width: Math.max(pxPerDay, right - left) };
  }

  return (
    <div className="tl-wrap">
      <div className="tl-toolbar">
        <div className="seg" role="group" aria-label="Zoom">
          {(["week", "month", "quarter"] as Zoom[]).map((z) => (
            <button
              key={z}
              type="button"
              className={zoom === z ? "seg-btn on" : "seg-btn"}
              aria-pressed={zoom === z}
              onClick={() => setZoom(z)}
            >
              {z.charAt(0).toUpperCase() + z.slice(1)}
            </button>
          ))}
        </div>
        <label className="t-sm t-muted" style={{ marginLeft: 8 }}>
          Group:{" "}
          <select
            className="select"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            aria-label="Group timeline by"
            style={{ width: 130 }}
          >
            <option value="none">None</option>
            <option value="assignee">Assignee</option>
            <option value="milestone">Milestone</option>
            <option value="epic">Epic</option>
          </select>
        </label>
      </div>

      <div className="tl-scroll">
        <div className="tl-grid" style={{ width: LABEL_W + width }}>
          {/* Left labels column */}
          <div className="tl-labels" style={{ width: LABEL_W }}>
            <div className="tl-corner" style={{ height: HEADER_H }} />
            {rows.map((r) =>
              r.kind === "group" ? (
                <div
                  key={r.key}
                  className="tl-row-label tl-group"
                  style={{ height: ROW_H }}
                >
                  {r.label}
                </div>
              ) : (
                <button
                  key={r.key}
                  type="button"
                  className="tl-row-label"
                  style={{ height: ROW_H }}
                  onClick={() =>
                    navigate({
                      to: "/tasks/$taskId",
                      params: { taskId: r.task.id },
                    })
                  }
                  title={r.task.title}
                >
                  {r.task.title}
                </button>
              ),
            )}
          </div>

          {/* Timeline canvas */}
          <div className="tl-canvas" style={{ width }}>
            <div className="tl-header" style={{ height: HEADER_H, width }}>
              {monthTicks.map((t, idx) => (
                <div key={idx} className="tl-month" style={{ left: t.x }}>
                  {t.label}
                </div>
              ))}
            </div>
            <div className="tl-body" style={{ height: bodyH, width }}>
              {/* month gridlines */}
              {monthTicks.map((t, idx) => (
                <div key={idx} className="tl-gridline" style={{ left: t.x }} />
              ))}
              {/* today line */}
              {todayX >= 0 && todayX <= width && (
                <div
                  className="tl-today"
                  style={{ left: todayX }}
                  title="Today"
                />
              )}
              {/* milestone markers */}
              {milestones.map((m) =>
                m.dueDate ? (
                  <div
                    key={m.id}
                    className="tl-milestone"
                    style={{ left: xForDate(m.dueDate) }}
                    title={`${m.name} (${m.dueDate})`}
                  >
                    <span className="tl-ms-flag">◆ {m.name}</span>
                  </div>
                ) : null,
              )}

              {/* dependency connectors */}
              <svg className="tl-deps" width={width} height={bodyH}>
                {edges.map((e, idx) => {
                  const bt = tasks.find((t) => t.id === e.blockerId);
                  const dt = tasks.find((t) => t.id === e.blockedId);
                  if (!bt || !dt) return null;
                  const bg = barGeom(bt);
                  const dg = barGeom(dt);
                  const by = taskRowY.get(e.blockerId);
                  const dy = taskRowY.get(e.blockedId);
                  if (!bg || !dg || by == null || dy == null) return null;
                  const x1 = bg.left + bg.width;
                  const y1 = by + ROW_H / 2;
                  const x2 = dg.left;
                  const y2 = dy + ROW_H / 2;
                  const critEdge =
                    criticalIds?.has(e.blockerId) &&
                    criticalIds?.has(e.blockedId);
                  return (
                    <path
                      key={idx}
                      d={`M ${x1} ${y1} C ${x1 + 18} ${y1}, ${x2 - 18} ${y2}, ${x2} ${y2}`}
                      className={`tl-dep-line${critEdge ? " critical" : ""}`}
                      markerEnd="url(#tl-arrow)"
                    />
                  );
                })}
                <defs>
                  <marker
                    id="tl-arrow"
                    markerWidth="7"
                    markerHeight="7"
                    refX="6"
                    refY="3"
                    orient="auto"
                  >
                    <path d="M0,0 L6,3 L0,6 Z" className="tl-arrow-head" />
                  </marker>
                </defs>
              </svg>

              {/* bars */}
              {rows.map((r) => {
                if (r.kind !== "task") return null;
                const g = barGeom(r.task);
                if (!g) return null;
                const isDragging = drag?.id === r.task.id;
                const dx = isDragging ? drag!.days * pxPerDay : 0;
                let left = g.left;
                let w = g.width;
                if (isDragging) {
                  if (drag!.mode === "move") left += dx;
                  else if (drag!.mode === "l") {
                    left += dx;
                    w -= dx;
                  } else w += dx;
                }
                const isCritical = criticalIds?.has(r.task.id);
                const pct = showProgress ? taskProgress(r.task) : null;
                return (
                  <div
                    key={r.task.id}
                    className={`tl-bar${isDragging ? " dragging" : ""}${isCritical ? " critical" : ""}`}
                    style={{
                      top: r.y + 5,
                      left,
                      width: Math.max(pxPerDay, w),
                      borderColor: isCritical
                        ? "#dc2626"
                        : typeMeta(r.task.type ?? "task").color,
                      background: typeMeta(r.task.type ?? "task").color,
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      setDrag({
                        id: r.task.id,
                        mode: "move",
                        startX: e.clientX,
                        days: 0,
                      });
                    }}
                    title={`${r.task.title}: ${r.task.startDate} → ${r.task.dueDate}`}
                  >
                    <span
                      className="tl-handle tl-handle-l"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDrag({
                          id: r.task.id,
                          mode: "l",
                          startX: e.clientX,
                          days: 0,
                        });
                      }}
                    />
                    {pct != null && pct > 0 && (
                      <span
                        className="tl-bar-progress"
                        style={{ width: `${pct}%` }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="tl-bar-label">
                      {r.task.title}
                      {pct != null ? ` · ${pct}%` : ""}
                    </span>
                    <span
                      className="tl-handle tl-handle-r"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDrag({
                          id: r.task.id,
                          mode: "r",
                          startX: e.clientX,
                          days: 0,
                        });
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {tasks.every((t) => !t.startDate || !t.dueDate) && (
        <p className="t-sm t-muted" style={{ marginTop: 10 }}>
          No tasks have both a start and due date yet — set them on a task to
          see its bar here.
        </p>
      )}
    </div>
  );
}
